import { createAsyncThunk } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/redux/store'
import {
  setConnectionStatus,
  addMessage,
  setError,
  setIsGenerating,
} from '@/redux/slices/testChatSlice'
import WebSocketManager from '@/services/websocketManager'

const wsManager = WebSocketManager.getInstance()

export const connectTestChatWebSocket = createAsyncThunk<
  void,
  { jwtToken: string; chatbotId: string; roomId: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/connectTestChat',
  async ({ jwtToken, chatbotId, roomId }, { rejectWithValue }) => {
    try {
      return await wsManager.connectToTestChat(jwtToken, chatbotId, roomId)
    } catch (error) {
      console.error('Failed to connect to test chat websocket:', error)
      return rejectWithValue(
        (error as Error).message || 'Failed to connect to test chat websocket'
      )
    }
  }
)

export const sendTestChatMessage = createAsyncThunk<
  void,
  { message: string; user?: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/sendTestChatMessage',
  async ({ message, user }, { rejectWithValue, dispatch }) => {
    dispatch(setIsGenerating(true))
    dispatch(
      addMessage({
        id: Date.now(),
        message: message,
        user: user || '',
        streaming: false,
      })
    )
    const success = wsManager.sendMessage('testChat', {
      type: 'test_message',
      message,
    })
    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const getPreviousChats = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/sendTestChatMessage', async (_, { rejectWithValue }) => {
  const success = wsManager.sendMessage('testChat', {
    type: 'previous_chat',
  })
  if (!success) {
    return rejectWithValue('WebSocket is not connected')
  }
})

export const closeTestChat = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/sendTestChatMessage', async (_, { rejectWithValue }) => {
  const success = wsManager.sendMessage('testChat', {
    type: 'test_chat_closed',
  })
  if (!success) {
    return rejectWithValue('WebSocket is not connected')
  }
})

export const saveTestChatMessages = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/saveTestChatMessages', async (_, { rejectWithValue }) => {
  try {
    const success = wsManager.sendMessage('testChat', {
      type: 'save_messages',
    })
    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  } catch (error) {
    return rejectWithValue(
      (error as Error).message || 'Failed to save test chat messages'
    )
  }
})

export const disconnectTestChatWebSocket = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/disconnectTestChat', async (_, { dispatch }) => {
  try {
    await wsManager.disconnect('testChat')
    dispatch(setConnectionStatus(false))
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Unknown error disconnecting test chat WebSocket'
    dispatch(setError(errorMessage))
  }
})
